/**
 * The full image-processing pipeline, as pure array maths plus OpenCV.
 *
 * Deliberately free of any DOM dependency: it takes RGBA bytes and returns RGBA
 * bytes. That is what lets the same code run inside a Web Worker (so a 4x upscale
 * does not freeze the page) *and* on the main thread as a fallback, without the
 * two paths drifting apart.
 *
 * The only canvas involvement left is PNG encoding, which the caller handles.
 */

import { inpaintRgba } from './inpaint';
import { stepUpscale } from './resample';
import { radiusToSigma, unsharpMask } from './sharpen';
import type { InpaintMethod } from './inpaint';

export interface PipelineOptions {
  /** 1, 2, 3 or 4. */
  scaleFactor: number;
  /** Unsharp mask strength, 0 to 1. Zero disables sharpening. */
  sharpenAmount: number;
  /** Masked regions to reconstruct. All-zero means "nothing to remove". */
  mask: Uint8Array;
  inpaintRadius: number;
  inpaintMethod: InpaintMethod;
}

export interface PipelineResult {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export type PipelineStage =
  | 'inpaint'
  | 'upscale'
  | 'sharpen-blur'
  | 'sharpen-combine'
  | 'encode';

export interface PipelineRequest {
  data: Uint8ClampedArray;
  width: number;
  height: number;
  options: PipelineOptions;
  onStage?: (stage: PipelineStage) => void;
}

/**
 * Remove the watermark, upscale, then sharpen — all at working resolution.
 *
 * Removal runs *before* upscaling. Doing it afterwards would judge the mask edge
 * against resampled pixels and smear the synthesised region.
 */
export async function runPipeline(request: PipelineRequest): Promise<PipelineResult> {
  const { data, width, height, options, onStage } = request;
  const { mask, scaleFactor, sharpenAmount } = options;

  let working = data;
  let workingW = width;
  let workingH = height;

  // --- 1. Inpaint ---------------------------------------------------------
  onStage?.('inpaint');
  working = await inpaintRgba(working, workingW, workingH, mask, {
    radius: options.inpaintRadius,
    method: options.inpaintMethod,
  });

  // --- 2. Upscale ---------------------------------------------------------
  onStage?.('upscale');
  const targetW = Math.max(1, Math.round(workingW * scaleFactor));
  const targetH = Math.max(1, Math.round(workingH * scaleFactor));

  if (targetW !== workingW || targetH !== workingH) {
    working = stepUpscale(working, workingW, workingH, targetW, targetH);
    workingW = targetW;
    workingH = targetH;
  }

  // --- 3. Sharpen ---------------------------------------------------------
  if (sharpenAmount > 0) {
    const sigma = radiusToSigma(0.8, scaleFactor);
    working = unsharpMask(working, workingW, workingH, sharpenAmount, sigma, (phase) =>
      onStage?.(phase === 'blur' ? 'sharpen-blur' : 'sharpen-combine')
    );
  }

  return { data: working, width: workingW, height: workingH };
}

/** True when there is at least one masked pixel to reconstruct. */
export function hasMaskWork(mask: Uint8Array | null): boolean {
  if (!mask) return false;
  for (let i = 0; i < mask.length; i++) if (mask[i]) return true;
  return false;
}